import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getToken } from './api';

interface SocketState {
  socket: Socket | null;
  connected: boolean;
  /**
   * Religa um socket que foi desconectado em DEFINITIVO por recusa de credencial.
   *
   * Existe por causa do ponto: a recusa `AUTH_PONTO_PENDENTE` e' terminal de proposito
   * (ver a lista abaixo), e ate 11/09/2026 quem a desfazia era a recarga de pagina do
   * sequestro de aba. Sem o sequestro ([[ponto-bloqueio.ts]]) ninguem mais recarrega nada,
   * entao a aba voltaria VIVA E SURDA — pior que o defeito que se foi consertar. Quem
   * chama e' o `AvisoPontoFechado`, no instante em que detecta a entrada batida.
   *
   * ⚠️ Nao reconecta o que ja esta conectado — e nao tem efeito nas outras recusas
   * terminais (dispositivo revogado, sessao encerrada, token invalido), que continuam
   * mortas ate alguem resolver a causa.
   */
  reconectar: () => void;
}

const SocketCtx = createContext<SocketState>({ socket: null, connected: false, reconectar: () => {} });

export function SocketProvider({ children }: { children: ReactNode }) {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  const reconectar = useCallback(() => {
    const s = socketRef.current;
    if (s && !s.connected) s.connect();
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) return;

    const socket = io('/', {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    // ⚠️ Recusa de CREDENCIAL nao se conserta tentando de novo — e por padrao o socket.io
    // tenta pra sempre. Com o aparelho revogado (15/08/2026) isso viraria uma batida a cada
    // poucos segundos, de um celular que pode estar na mao de quem levou ele. Erro de REDE
    // continua reconectando normalmente: e' o caso em que insistir e' exatamente o certo.
    socket.on('connect_error', (err: Error) => {
      const terminal =
        err.message === 'AUTH_DISPOSITIVO_NAO_AUTORIZADO' ||
        // Nao bateu o ponto: insistir nao muda nada enquanto a entrada nao for batida.
        // ⚠️ Quem religa e' o `reconectar` acima, chamado pelo `AvisoPontoFechado` quando
        // a entrada volta — ate 11/09/2026 quem fazia isso era a recarga de pagina do
        // sequestro de aba, que nao existe mais.
        err.message === 'AUTH_PONTO_PENDENTE' ||
        err.message === 'AUTH_SESSAO_ENCERRADA' ||
        err.message === 'AUTH_TOKEN_INVALIDO' ||
        err.message === 'AUTH_TOKEN_AUSENTE';
      if (terminal) socket.disconnect();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  return (
    <SocketCtx.Provider value={{ socket: socketRef.current, connected, reconectar }}>
      {children}
    </SocketCtx.Provider>
  );
}

export function useSocket(): SocketState {
  return useContext(SocketCtx);
}
